import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { toast } from 'react-toastify'
import Select from 'react-select'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import { FaSave, FaSpinner, FaCheck, FaTimes, FaEye } from 'react-icons/fa'
import { useParams, useNavigate } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import './EmployeeGoals.css'
import { useLocation } from 'react-router-dom';
import API_BASE_URL from '../../../config/api'

const EmployeeGoals = () => {
  const baseUrl = API_BASE_URL
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation();
  const employeeId = location.state?.employeeId || id; // fallback to URL param
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingEmployee, setFetchingEmployee] = useState(true)
  const [goals, setGoals] = useState([])
  const [employee, setEmployee] = useState(null)
  const [expandedTexts, setExpandedTexts] = useState({})
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedGoal, setSelectedGoal] = useState(null)

  const handleViewGoal = (goal) => {
    setSelectedGoal(goal)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedGoal(null)
  }

  const toggleTextExpansion = (goalId, field) => {
    setExpandedTexts(prev => ({
      ...prev,
      [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
    }))
  }

  const renderTruncatedText = (text, goalId, field, maxLength = 55) => {
    const safeText = (text ?? '').toString();
    if (!safeText) return '';
    if (safeText.length <= maxLength) return safeText;

    const key = `${goalId || 'unknown'}-${field}`;
    const isExpanded = !!expandedTexts[key];
    const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`;

    return (
      <span style={{ display: 'inline' }}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
        <button
          type="button"
          onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
          style={{
            marginLeft: 8,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#2563eb',
            fontWeight: 600,
            fontSize: 12,
            textDecoration: 'underline',
          }}
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  }

  useEffect(() => {
    const fetchEmployee = async () => {
      setFetchingEmployee(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        console.log(response.data.employee)
        const emp = response.data.employee
        setEmployee(emp)
        setGoals(emp.goals || [])
      } catch (err) {
        toast.error('Failed to fetch employee data.')
      } finally {
        setFetchingEmployee(false)
      }
    }
    if (employeeId) fetchEmployee()
  }, [employeeId])

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="page-title">
              <h1>Employee Key Perfomance Indicators</h1>
              <p>View KPIs for this employee</p>
            </div>
            <button
              style={{
                background: '#4F46E5',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '6px 18px',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                transition: 'background 0.2s',
              }}
              onClick={() => {
                if (typeof window !== 'undefined' && window.history) {
                  window.history.back();
                }
              }}
            >
              Back to All Employees
            </button>
          </div>
        </div>
        <div className="hr-content-wrapper">
          {fetchingEmployee ? (
            <div className="loading-state"><FaSpinner className="spinner" /> Loading employee data...</div>
          ) : employee ? (
            <>
              <div className="employee-info-card">
                <div><strong>Name:</strong> {employee.first_name} {employee.last_name}</div>
                <div><strong>Position:</strong> {employee.position}</div>
              </div>
              <div className="goals-table-wrapper">
                <table className="goals-table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Stakeholder</th>
                      <th>KPI Metric</th>
                      <th>RAG</th>
                      <th>Quarter</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goals.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-row">
                          No goals found for this employee.
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        // Group employee goals by KRA, similar to HRGoals
                        const groupedByKra = goals.reduce((acc, goal) => {
                          const kraName =
                            (goal.key_result_area && typeof goal.key_result_area === 'object'
                              ? goal.key_result_area.name
                              : goal.key_result_area || goal.name) || 'Uncategorized';
                          if (!acc[kraName]) acc[kraName] = [];
                          acc[kraName].push(goal);
                          return acc;
                        }, {});

                        return Object.entries(groupedByKra).map(([kraName, kraGoals]) => (
                          <React.Fragment key={kraName}>
                            <tr className="kra-group-row">
                              <td colSpan={6} style={{ fontWeight: 700, background: '#f1f5f9' }}>
                                {(() => {
                                  const kraPercentage =
                                    kraGoals?.[0]?.key_result_area?.percentage ??
                                    kraGoals?.[0]?.kra?.percentage ??
                                    kraGoals?.[0]?.percentage;
                                  return (
                                    <>
                                      {kraName}
                                      {' '}
                                      <span style={{ fontWeight: 800, color: '#475569' }}>
                                        (KRA Weight: {kraPercentage !== undefined && kraPercentage !== null ? `${kraPercentage}%` : '-'})
                                      </span>
                                    </>
                                  );
                                })()}
                              </td>
                            </tr>
                            {kraGoals.map((goal, idx) => (
                              <tr key={goal.id || `${kraName}-${idx}`}>
                                <td>
                                  {renderTruncatedText(goal.activity, goal.id || `${kraName}-${idx}`, 'activity')}
                                </td>
                                <td>
                                  {renderTruncatedText(
                                    goal.stakeholder,
                                    goal.id || `${kraName}-${idx}`,
                                    'stakeholder',
                                    35
                                  )}
                                </td>
                                <td>{goal.metric}</td>
                                <td>
                                  <div className="rag-bar-bg">
                                    <div
                                      className="rag-bar-fill"
                                      style={{ backgroundColor: goal.progress_indicator || '#e5e7eb', width: '100%' }}
                                    >
                                      <span className="rag-bar-label">{goal.rag_color || ''}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>{goal.quarter}</td>
                                <td>
                                  <span className={`status-badge status-${goal.approval_status?.toLowerCase() || 'unknown'}`}>
                                    {goal.approval_status === 'pending' && <FaSpinner className="pending-spinner" />}
                                    {goal.approval_status
                                      ? goal.approval_status.charAt(0).toUpperCase() + goal.approval_status.slice(1)
                                      : ''}
                                  </span>
                                </td>
                                <td>
                                  <div className="goal-actions">
                                    <button
                                      className="action-button view-button"
                                      title="View"
                                      onClick={() => handleViewGoal(goal)}
                                    >
                                      <FaEye />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ));
                      })()
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="error-state">Employee not found.</div>
          )}
        </div>
      </div>

      {modalOpen && selectedGoal && (
        <div className="modal-overlay">
          <div className="modal-content-employee-goal">
            <div className="modal-header">
              <h2 style={{color: '#fff'}}>KPI Details</h2>
            </div>
            <div className="modal-body">
              <div className="goal-details-grid">
                <div className="goal-detail-row">
                  <label>Key Result Area:</label>
                  <span>{selectedGoal.key_result_area?.name || selectedGoal.name}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Key Result Area Weight:</label>
                  <span>{selectedGoal.key_result_area?.percentage || selectedGoal.percentage}%</span>
                </div>
                <div className="goal-detail-row">
                  <label>Activity:</label>
                  <span>{selectedGoal.activity}</span>
                </div>
                <div className="goal-detail-row">
                  <label>KPI Metric:</label>
                  <span>{selectedGoal.metric}%</span>
                </div>
                <div className="goal-detail-row">
                  <label>Stakeholder:</label>
                  <span>{selectedGoal.stakeholder}</span>
                </div>
                <div className="goal-detail-row">
                  <label>Quarter:</label>
                  <span>{selectedGoal.quarter}</span>
                </div>
                <div className="goal-detail-row">
                  <label>RAG Color:</label>
                  <div className="progress-indicator-display" style={{ backgroundColor: selectedGoal.progress_indicator || '#e5e7eb', width: '100%' }}>
                    
                  </div>
                </div>
                <div className="goal-detail-row">
                  <label>Completion Status:</label>
                  <span>
                    {selectedGoal.status 
                      ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ')
                      : 'N/A'}
                  </span>
                </div>
                <div className="goal-detail-row">
                  <label>Is Overdue:</label>
                  <span className={selectedGoal.is_overdue ? 'overdue-yes' : 'overdue-no'}>
                    {selectedGoal.is_overdue ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="goal-detail-row">
                  <label>Approval Status:</label>
                  <span style={{color: '#ffc'}} className={`status-badge status-${selectedGoal.approval_status?.toLowerCase() || 'unknown'}`}>
                    {selectedGoal.approval_status === 'pending' && <FaSpinner className="pending-spinner" />}
                    {selectedGoal.approval_status ? selectedGoal.approval_status.charAt(0).toUpperCase() + selectedGoal.approval_status.slice(1) : 'N/A'}
                  </span>
                </div>
                <div className="goal-detail-row">
                  <label>Due Date:</label>
                  <span>
                    {selectedGoal.completion_date 
                      ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                      : 'N/A'}
                  </span>
                </div>
                {selectedGoal.approval_status === 'rejected' && selectedGoal.rejected_reason && (
                  <div className="goal-detail-row full-width">
                    <label>Rejection Reason:</label>
                    <span className="rejection-reason">{selectedGoal.rejected_reason}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="modal-close-button">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EmployeeGoals
